import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { Hash, Lock } from "lucide-mui";
import { Link as RouterLink } from "react-router-dom";
import { api, type SlackChannelOption } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";

/** The channel a Slack step posts to, from the tenant's org's workspace. */
export default function SlackChannelPicker({
  tenantId,
  channelId,
  channelName,
  onChange,
  disabled,
}: {
  tenantId: string;
  channelId: string;
  channelName: string;
  onChange: (channel: SlackChannelOption | null) => void;
  disabled?: boolean;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: keys.slackChannels.byTenant(tenantId),
    queryFn: () => api.getSlackChannels(tenantId),
    enabled: tenantId.length > 0,
  });

  if (data && !data.connected) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        The tenant's organization has no Slack workspace connected.{" "}
        <RouterLink to="/organization/integrations">Connect Slack</RouterLink> first.
      </Typography>
    );
  }

  if (data?.needs_reauth) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        The Slack connection is missing a permission needed to list channels.{" "}
        <RouterLink to="/organization/integrations">Reconnect Slack</RouterLink>
      </Typography>
    );
  }

  const channels = data?.channels ?? [];
  // Keep showing the saved channel even if it is not listed (yet).
  const selected =
    channels.find((c) => c.id === channelId) ??
    (channelId ? { id: channelId, name: channelName || channelId, is_private: false } : null);

  return (
    <Autocomplete
      size="small"
      fullWidth
      options={channels}
      loading={isLoading}
      value={selected}
      disabled={disabled}
      onChange={(_, value) => onChange(value)}
      getOptionLabel={(c) => c.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderOption={(props, c) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            {c.is_private ? (
              <Lock fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
            ) : (
              <Hash fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
            )}
            {c.name}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={data?.team_name ? `Slack channel (${data.team_name})` : "Slack channel"}
          error={!!error || !channelId}
          helperText={
            error
              ? "Could not list Slack channels."
              : "Private channels appear once you invite the bot to them."
          }
        />
      )}
    />
  );
}
